import { fetchAuthSession } from 'aws-amplify/auth';
import { Session, SessionSummary, ChildLink, SseEvent, IntakeProfile, TeacherInfo, SessionListItem, StudioProject, ProjectFeedback, ProjectType } from '../types';

const API_BASE = import.meta.env.VITE_API_URL ?? '/api';
const CHAT_URL = import.meta.env.VITE_CHAT_URL ?? '/api/chat';

async function getAuthHeader(): Promise<string | undefined> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString();
    return token ? `Bearer ${token}` : undefined;
  } catch {
    return undefined;
  }
}

export async function createSession(profile?: IntakeProfile): Promise<{
  sessionId: string;
  initialMessage: string;
  phase: 'counselor' | 'teacher';
  teacher?: TeacherInfo;
}> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/sessions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(authHeader ? { Authorization: authHeader } : {}),
    },
    body: profile ? JSON.stringify({ profile }) : undefined,
  });
  if (res.status === 402) {
    const data = await res.json().catch(() => ({})) as { error?: string; message?: string };
    const err = new Error(data.message ?? 'Session limit reached');
    (err as Error & { code: string }).code = 'session_limit_reached';
    throw err;
  }
  if (!res.ok) throw new Error('Failed to create session');
  return res.json();
}

export async function createCheckInSession(): Promise<{ sessionId: string; initialMessage: string; phase: 'counselor'; checkIn: true }> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify({ checkin: true }),
  });
  if (!res.ok) throw new Error('Failed to start check-in');
  return res.json();
}

export async function getSession(sessionId: string): Promise<Session> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/sessions/${sessionId}`, {
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (!res.ok) throw new Error('Session not found');
  return res.json();
}

export interface QuizQuestion {
  question: string;
  options: [string, string, string, string];
  correct: number;
  explanation: string;
}

// Baseline placement diagnostic — runs once per (student, subject) before
// their first session in that subject, so the teacher starts calibrated
// instead of guessing from a self-reported confidence level.
export async function getDiagnostic(subject: string, gradeLevel: string): Promise<{ needed: boolean; questions?: QuizQuestion[] }> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/student/diagnostic`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify({ subject, gradeLevel }),
  });
  if (!res.ok) return { needed: false }; // fail open — never block session creation on the diagnostic
  return res.json();
}

export async function saveDiagnosticResult(subject: string, correct: number, total: number): Promise<{ level: string }> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/student/diagnostic/result`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify({ subject, correct, total }),
  });
  if (!res.ok) throw new Error('Failed to save diagnostic result');
  return res.json();
}

export async function endSession(
  sessionId: string,
  options?: { quiz?: boolean }
): Promise<{ summary: SessionSummary | null; quiz?: QuizQuestion[] }> {
  const authHeader = await getAuthHeader();
  const qs = options?.quiz ? '?quiz=true' : '';
  const res = await fetch(`${API_BASE}/sessions/${sessionId}/end${qs}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(authHeader ? { Authorization: authHeader } : {}),
    },
  });
  if (!res.ok) throw new Error('Failed to end session');
  return res.json() as Promise<{ summary: SessionSummary | null; quiz?: QuizQuestion[] }>;
}

// Fire-and-forget summary generation for when the student LEAVES the chat
// without clicking "End session" (navigate away / close tab). Uses keepalive so
// the request survives page unload; the summary is generated + saved server-side
// and the parent is notified, exactly like an explicit end (no quiz). Best-effort
// — errors are swallowed since there's no UI to show them to.
export async function endSessionKeepalive(sessionId: string): Promise<void> {
  try {
    const authHeader = await getAuthHeader();
    await fetch(`${API_BASE}/sessions/${sessionId}/end`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
      keepalive: true,
    });
  } catch { /* best-effort */ }
}

// ─── Parent child-linking ─────────────────────────────────────────────────────

export async function createChild(payload: {
  childName: string; childEmail: string; gradeLevel: string; relationship?: string; password: string;
}): Promise<{ childId: string; childName: string; childEmail: string; gradeLevel: string }> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/parent/children`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? 'Failed to create child account');
  }
  const data = await res.json() as { child: { childId: string; childName: string; childEmail: string; gradeLevel: string } };
  return data.child;
}

export async function listChildren(): Promise<ChildLink[]> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/parent/children`, {
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (!res.ok) throw new Error('Failed to fetch children');
  const data = await res.json() as { children: ChildLink[] };
  return data.children;
}

export async function fetchChildSessions(childId: string): Promise<{
  child: ChildLink;
  sessions: (Omit<Session, 'messages'>)[];
}> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/parent/children/${childId}/sessions`, {
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (!res.ok) throw new Error('Failed to fetch child sessions');
  return res.json() as Promise<{ child: ChildLink; sessions: (Omit<Session, 'messages'>)[] }>;
}

export interface TranscriptTurn { role: 'user' | 'assistant'; content: string; speaker: string; }

// Full conversation transcript of one of a parent's own child's sessions —
// maximum transparency for building trust. Ownership enforced server-side.
export async function fetchChildSessionTranscript(childId: string, sessionId: string): Promise<{
  transcript: TranscriptTurn[];
  teacherName: string;
  subject: string | null;
  createdAt: string;
}> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/parent/children/${childId}/sessions?sessionId=${encodeURIComponent(sessionId)}`, {
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (!res.ok) throw new Error('Failed to fetch transcript');
  return res.json() as Promise<{ transcript: TranscriptTurn[]; teacherName: string; subject: string | null; createdAt: string }>;
}

// ─── Parental controls ────────────────────────────────────────────────────────

export interface ParentalControls {
  notifyOnSession: boolean;
  allowedTeachers: string[] | null;
  dailySessionLimit: number | null;
  remindersOptOut?: boolean; // opt out of schedule/reminder emails (stored on child profile)
}

export async function getParentalControls(childId: string): Promise<{ controls: ParentalControls; childName: string }> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/parent/children/${childId}/controls`, {
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (!res.ok) throw new Error('Failed to fetch controls');
  return res.json() as Promise<{ controls: ParentalControls; childName: string }>;
}

export async function updateParentalControls(
  childId: string,
  controls: Partial<ParentalControls>
): Promise<void> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/parent/children/${childId}/controls`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify(controls),
  });
  if (!res.ok) throw new Error('Failed to update controls');
}

// ─── Admin ────────────────────────────────────────────────────────────────────

export interface AdminBridgeEnrollment {
  programName: string;
  status: string;
  week: number;
  totalWeeks: number;
}

export interface AdminUser {
  username: string;
  email: string;
  status: string;
  enabled: boolean;
  groups: string[];
  createdAt: string;
  plan?: string;                 // 'free' | 'student' | 'family'
  planStatus?: string;           // 'active' | 'trialing' | 'past_due' | 'cancelled' | 'none'
  planRenewsAt?: string | null;
  bridgeCredits?: number;
  bridgeEnrollments?: AdminBridgeEnrollment[];
}

export interface AdminUserCounts {
  total: number;
  students: number;
  parents: number;
  admins: number;
  pending: number;
  paidPlans?: number;
  bridgeEnrolled?: number;
}

export async function adminListUsers(): Promise<{ users: AdminUser[]; counts: AdminUserCounts }> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/admin/users`, {
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (!res.ok) throw new Error('Failed to fetch users');
  return res.json() as Promise<{ users: AdminUser[]; counts: AdminUserCounts }>;
}

export async function adminSetUserGroup(targetUsername: string, group: string): Promise<void> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/admin/users/group`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify({ targetUsername, group, action: 'setGroup' }),
  });
  if (!res.ok) throw new Error('Failed to update user group');
}

export async function adminToggleUser(targetUsername: string, enable: boolean): Promise<void> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/admin/users/group`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify({ targetUsername, action: enable ? 'enable' : 'disable' }),
  });
  if (!res.ok) throw new Error('Failed to update user');
}

// ─── Studio ───────────────────────────────────────────────────────────────────

export async function createStudioProject(title: string, type: ProjectType): Promise<StudioProject> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/student/studio/projects`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify({ title, type }),
  });
  if (!res.ok) throw new Error('Failed to create project');
  const data = await res.json() as { project: StudioProject };
  return data.project;
}

export async function listStudioProjects(): Promise<StudioProject[]> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/student/studio/projects`, {
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (!res.ok) throw new Error('Failed to list projects');
  const data = await res.json() as { projects: StudioProject[] };
  return data.projects;
}

export async function getStudioProject(projectId: string): Promise<StudioProject> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/student/studio/projects/${projectId}`, {
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (!res.ok) throw new Error('Project not found');
  const data = await res.json() as { project: StudioProject };
  return data.project;
}

export async function updateStudioProject(projectId: string, updates: { title?: string; content?: string; status?: 'draft' | 'portfolio' }): Promise<void> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/student/studio/projects/${projectId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error('Failed to update project');
}

export async function requestProjectFeedback(projectId: string): Promise<ProjectFeedback> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/student/studio/projects/${projectId}/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
  });
  if (!res.ok) throw new Error('Failed to generate feedback');
  const data = await res.json() as { feedback: ProjectFeedback };
  return data.feedback;
}

// Ask-your-teacher coaching for an in-progress project (not persisted).
export async function askProjectGuidance(projectId: string, question: string): Promise<{ answer: string; teacherName: string }> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/student/studio/projects/${projectId}/guidance`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify({ question }),
  });
  if (!res.ok) throw new Error('Failed to get guidance');
  return await res.json() as { answer: string; teacherName: string };
}

export async function getChildData(childId: string): Promise<{
  childId: string;
  childName: string;
  link: { linkedAt: string; notifyOnSession?: boolean; allowedTeachers?: string[]; dailySessionLimit?: number };
  profile: object | null;
  sessions: { sessionId: string; createdAt: string; subject: string | null; gradeLevel: string | null; teacher: string | null; summary: object | null }[];
  totalSessions: number;
  dataCategories: string[];
}> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/parent/children/${childId}/data`, {
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (!res.ok) throw new Error('Failed to retrieve child data');
  return res.json();
}

export async function deleteChildAccount(childId: string): Promise<{ deleted: boolean; childName: string; sessionsDeleted: number }> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/parent/children/${childId}/account`, {
    method: 'DELETE',
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (!res.ok) throw new Error('Failed to delete child account');
  return res.json();
}

export async function getChildPortfolio(childId: string): Promise<{ projects: StudioProject[]; childName: string }> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/parent/children/${childId}/portfolio`, {
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (!res.ok) throw new Error('Failed to fetch portfolio');
  return res.json() as Promise<{ projects: StudioProject[]; childName: string }>;
}

// ─── Student session history ──────────────────────────────────────────────────

export async function getStudentSessions(limit = 20): Promise<SessionListItem[]> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/student/sessions?limit=${limit}`, {
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (!res.ok) throw new Error('Failed to fetch sessions');
  const data = await res.json() as { sessions: SessionListItem[] };
  return data.sessions;
}

// ─── Student profile ─────────────────────────────────────────────────────────

export interface StudentProfile {
  userId: string;
  grade?: string;
  subjects?: string[];
  firstName?: string;
  learnerType?: string;
  shortTermGoal?: string;
  longTermGoal?: string;
  urgency?: string;
  weeklyHours?: string;
  preferredDays?: string[];
  interests?: string[];
  careerGoal?: string;
  schedule?: object[];
  keiMessage?: string;
  remindersOptOut?: boolean;
  parentFocus?: string;                         // parent's focus request for the teachers
  parentEncouragement?: { text: string; at: string }; // note from a parent, shown to the child
  levels?: SubjectLevelProgress[];              // felt-progress goal loop — climb toward the next mastery level
  createdAt?: string;
  updatedAt?: string;
}

export interface SubjectLevelProgress {
  subject: string;
  level: 'Foundation' | 'Standard' | 'Accelerated';
  nextLevel: 'Foundation' | 'Standard' | 'Accelerated' | null;
  progressPct: number; // 0-100 toward next level
  atTop: boolean;
}

export async function getStudentProfile(): Promise<StudentProfile | null> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/student/profile`, {
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Failed to fetch profile');
  const data = await res.json() as { profile: StudentProfile };
  return data.profile;
}

export interface CareerGuidance {
  focusThisYear: string;
  nextSteps: string[];
}

// Personalized, grade-aware next steps for the student's saved career goal.
// Throws on 409 (no career goal set) or any error — the caller shows a friendly
// message. The AI is grounded server-side to real orgs only.
export async function getCareerGuidance(): Promise<CareerGuidance> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/student/career-guidance`, {
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (!res.ok) throw new Error('Failed to fetch career guidance');
  return await res.json() as CareerGuidance;
}

// ─── Student onboarding ───────────────────────────────────────────────────────

export interface LearningPlanPayload {
  type: 'quick' | 'plan' | 'edit' | 'rebuild-schedule';
  firstName?: string;
  grade?: string;
  learnerType?: 'homework' | 'explore' | 'both';
  subjects?: string[];
  shortTermGoal?: string;
  longTermGoal?: string;
  urgency?: string;
  weeklyHours?: string;
  preferredDays?: string[];
  interests?: string[];
  careerGoal?: string;
  remindersOptOut?: boolean;
}

export interface ScheduleItem {
  teacher: string;
  subject: string;
  daysPerWeek: number;
  hoursPerSession: number;
  suggestedDays: string[];
  focus: string;
}

export async function saveStudentProfile(payload: LearningPlanPayload): Promise<{
  success: boolean;
  keiMessage?: string;
  schedule?: ScheduleItem[];
}> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/student/profile`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? 'Failed to save profile');
  }
  return res.json() as Promise<{ success: boolean; keiMessage?: string; schedule?: ScheduleItem[] }>;
}

// Regenerate the recurring schedule from the student's saved subjects
// (overwrites existing slots). Returns the fresh slots + Mrs. Kei's note.
export async function rebuildSchedule(): Promise<{ slots: RecurringSlot[]; keiMessage?: string }> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/student/profile`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify({ type: 'rebuild-schedule' }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? 'Failed to rebuild schedule');
  }
  return res.json() as Promise<{ slots: RecurringSlot[]; keiMessage?: string }>;
}

// ─── Billing ─────────────────────────────────────────────────────────────────

export interface SubscriptionInfo {
  plan: 'free' | 'student' | 'family';
  status: 'active' | 'trialing' | 'past_due' | 'cancelled' | 'none';
  sessionsUsed: number;
  sessionsLimit: number | null;
  sessionsRemaining: number | null;
  currentPeriodEnd: string | null;
  hasActiveSubscription: boolean;
  bridgeCreditsAvailable?: number; // backend always sends it; optional so local fallback literals need not
  hasBillingAccount?: boolean; // true only for Stripe-purchased plans (comped plans can't open the billing portal)
}

export async function getSubscriptionInfo(): Promise<SubscriptionInfo> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/billing/subscription`, {
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (!res.ok) throw new Error('Failed to fetch subscription');
  return res.json() as Promise<SubscriptionInfo>;
}

export async function createCheckoutSession(plan: 'student' | 'family', returnPath: string): Promise<string> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/billing/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify({ plan, returnPath }),
  });
  if (!res.ok) throw new Error('Failed to create checkout session');
  const data = await res.json() as { url: string };
  return data.url;
}

// ─── Onboarding journey ───────────────────────────────────────────────────────

export interface ParentContext {
  parentGoal?: string;
  parentFirstName?: string;
  parentRelationship?: string;
  childFirstName?: string;
  childGrade?: string;
}

export async function getParentContext(): Promise<ParentContext | null> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/student/parent-context`, {
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (!res.ok) return null;
  return res.json() as Promise<ParentContext>;
}

export async function notifyFirstLogin(): Promise<void> {
  const authHeader = await getAuthHeader();
  await fetch(`${API_BASE}/student/first-login-notify`, {
    method: 'POST',
    headers: authHeader ? { Authorization: authHeader } : {},
  });
}

export async function saveParentGoal(goal: string): Promise<void> {
  const authHeader = await getAuthHeader();
  await fetch(`${API_BASE}/parent/goal`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify({ goal }),
  });
}

// ─── Error reporting ─────────────────────────────────────────────────────────

export interface ErrorReportPayload {
  severity: 'critical' | 'high' | 'medium' | 'low';
  category: 'auth' | 'payment' | 'api' | 'frontend' | 'session' | 'system';
  message: string;
  details?: Record<string, unknown>;
  userId?: string;
  userEmail?: string;
  url?: string;
}

export async function reportError(payload: ErrorReportPayload): Promise<void> {
  try {
    const authHeader = await getAuthHeader().catch(() => undefined);
    await fetch(`${API_BASE}/errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
      body: JSON.stringify(payload),
    });
  } catch {
    // Silent fail — error reporting must never cause additional errors
  }
}

export async function getErrorTickets(opts?: { status?: string; severity?: string; category?: string }): Promise<{ tickets: object[]; total: number }> {
  const authHeader = await getAuthHeader();
  const qs = new URLSearchParams(opts as Record<string, string> ?? {}).toString();
  const res = await fetch(`${API_BASE}/admin/errors${qs ? `?${qs}` : ''}`, {
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (!res.ok) throw new Error('Failed to fetch error tickets');
  return res.json() as Promise<{ tickets: object[]; total: number }>;
}

export async function updateErrorTicketStatus(ticketId: string, status: 'open' | 'acknowledged' | 'resolved'): Promise<void> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/admin/errors`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify({ ticketId, status }),
  });
  if (!res.ok) throw new Error('Failed to update ticket');
}

// ─── Bridge ───────────────────────────────────────────────────────────────────

export async function createBridgeCheckout(programId: string, childId: string): Promise<{ url: string; enrollmentId: string }> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/parent/bridge/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify({ programId, childId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? 'Failed to create bridge checkout');
  }
  return res.json() as Promise<{ url: string; enrollmentId: string }>;
}

export async function confirmBridgeEnrollment(stripeSessionId: string, enrollmentId: string): Promise<{ enrollment: object }> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/parent/bridge/success?session_id=${stripeSessionId}&enrollment_id=${enrollmentId}`, {
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (!res.ok) throw new Error('Failed to confirm enrollment');
  return res.json() as Promise<{ enrollment: object }>;
}

export async function getBridgeEnrollments(): Promise<{ enrollments: object[] }> {
  const authHeader = await getAuthHeader();
  // Works for both parent (/parent/bridge/enrollments) and student (/student/bridge)
  // We call student endpoint first as it's user-agnostic from the Lambda's perspective
  const url = `${API_BASE}/student/bridge`;
  const res = await fetch(url, {
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (!res.ok) throw new Error('Failed to fetch bridge enrollments');
  return res.json() as Promise<{ enrollments: object[] }>;
}

export async function getParentBridgeEnrollments(): Promise<{ enrollments: object[] }> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/parent/bridge/enrollments`, {
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (!res.ok) throw new Error('Failed to fetch bridge enrollments');
  return res.json() as Promise<{ enrollments: object[] }>;
}

// Remove an abandoned/unpaid Bridge enrollment (parent self-service).
export async function removeBridgeEnrollment(enrollmentId: string): Promise<void> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/parent/bridge/enrollments/${encodeURIComponent(enrollmentId)}`, {
    method: 'DELETE',
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? 'Failed to remove enrollment');
  }
}

export async function saveBridgeDiagnostic(enrollmentId: string, answers: number[]): Promise<{ track: string }> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/student/bridge/${enrollmentId}/diagnostic`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify({ answers }),
  });
  if (!res.ok) throw new Error('Failed to save diagnostic');
  return res.json() as Promise<{ track: string }>;
}

export async function getBillingPortalUrl(returnPath: string): Promise<string> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/billing/portal`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify({ returnPath }),
  });
  if (!res.ok) throw new Error('Failed to create billing portal session');
  const data = await res.json() as { url: string };
  return data.url;
}

export async function* streamChat(
  sessionId: string,
  message: string,
  useCounselor = false,
  image?: { data: string; mediaType: string }
): AsyncGenerator<SseEvent> {
  const authHeader = await getAuthHeader();
  const res = await fetch(CHAT_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(authHeader ? { Authorization: authHeader } : {}),
    },
    body: JSON.stringify({
      sessionId,
      message,
      useCounselor: useCounselor || undefined,
      imageData: image?.data,
      imageMediaType: image?.mediaType,
    }),
  });

  if (!res.ok || !res.body) throw new Error('Chat request failed');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        const jsonStr = line.slice(6).trim();
        if (jsonStr) {
          try { yield JSON.parse(jsonStr) as SseEvent; } catch { /* skip */ }
        }
      }
    }
  }

  if (buffer.startsWith('data: ')) {
    const jsonStr = buffer.slice(6).trim();
    if (jsonStr) {
      try { yield JSON.parse(jsonStr) as SseEvent; } catch { /* ignore */ }
    }
  }
}

// ─── Generic auth helpers ────────────────────────────────────────────────────

export async function apiGet<T>(path: string, qs?: Record<string, string>): Promise<T> {
  const authHeader = await getAuthHeader();
  const query = qs ? '?' + new URLSearchParams(qs).toString() : '';
  const res = await fetch(`${API_BASE}${path}${query}`, {
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (!res.ok) throw new Error(`GET ${path} failed: ${res.status}`);
  return res.json() as Promise<T>;
}

export async function apiPut<T>(path: string, body: unknown): Promise<T> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`PUT ${path} failed: ${res.status}`);
  return res.json() as Promise<T>;
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`POST ${path} failed: ${res.status}`);
  return res.json() as Promise<T>;
}

export async function apiDelete(path: string): Promise<void> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'DELETE',
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (!res.ok) throw new Error(`DELETE ${path} failed: ${res.status}`);
}

export async function adminDeleteUser(username: string, email: string): Promise<void> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/admin/users/delete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify({ targetUsername: username, targetEmail: email }),
  });
  if (!res.ok) throw new Error('Failed to delete user');
}

// Unblock an UNCONFIRMED account: re-send the sign-up confirmation email so the
// user can verify it themselves.
export async function adminResendConfirmation(username: string, email: string): Promise<{ ok: boolean; delivery?: string }> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/admin/users/confirmation`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify({ targetUserId: username, targetEmail: email, action: 'resend' }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? 'Failed to resend confirmation email');
  }
  return res.json() as Promise<{ ok: boolean; delivery?: string }>;
}

// Force-confirm an UNCONFIRMED account and mark its email verified, so a known
// contact can log in immediately without the emailed code.
export async function adminConfirmUser(username: string, email: string): Promise<void> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/admin/users/confirmation`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify({ targetUserId: username, targetEmail: email, action: 'confirm' }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? 'Failed to confirm user');
  }
}

// ─── Bridge credit (parent assigns admin-granted credit to a child) ───────────

export async function resetChildPassword(childId: string, childEmail: string, newPassword: string): Promise<void> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/parent/children/${childId}/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify({ childId, childEmail, newPassword }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? 'Failed to reset password');
  }
}

export async function useBridgeCredit(childId: string, childName: string, programId: string): Promise<{ enrollment: object }> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/parent/bridge/use-credit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify({ childId, childName, programId }),
  });
  if (!res.ok) throw new Error('Failed to use bridge credit');
  return res.json() as Promise<{ enrollment: object }>;
}

// ─── Student recurring schedule ───────────────────────────────────────────────

export interface RecurringSlot {
  slotId: string;
  teacherName: string;
  subject: string;
  emoji: string;
  days: string[];
  timeOfDay: 'morning' | 'afternoon' | 'evening';
  time?: string; // optional exact "HH:MM" (24h) — enables timed reminders
  createdAt: string;
}

export async function getRecurringSlots(): Promise<RecurringSlot[]> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/student/schedule`, {
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (!res.ok) throw new Error('Failed to fetch schedule');
  const data = await res.json() as { slots: RecurringSlot[] };
  return data.slots;
}

export async function addRecurringSlot(slot: Omit<RecurringSlot, 'slotId' | 'createdAt'>): Promise<RecurringSlot> {
  const authHeader = await getAuthHeader();
  // Capture the browser's IANA timezone so the backend can fire an exact-time
  // reminder at the right absolute moment for this student.
  let timezone: string | undefined;
  try { timezone = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { /* older browser — backend falls back to ET */ }
  const res = await fetch(`${API_BASE}/student/schedule`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify({ ...slot, ...(timezone ? { timezone } : {}) }),
  });
  if (!res.ok) throw new Error('Failed to add slot');
  const data = await res.json() as { slot: RecurringSlot };
  return data.slot;
}

export async function deleteRecurringSlot(slotId: string): Promise<void> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/student/schedule/${slotId}`, {
    method: 'DELETE',
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (!res.ok) throw new Error('Failed to delete slot');
}

// ─── Mixed Practice worksheet ─────────────────────────────────────────────────

export interface WorksheetQuestion {
  subject: string;
  question: string;
  options: [string, string, string, string];
  correct: number;
  explanation: string;
}

export type WorksheetResult =
  | { status: 'ok'; questions: WorksheetQuestion[]; subjects: string[] }
  | { status: 'no_sessions'; message: string }
  | { status: 'error'; message: string };

export async function generateWorksheet(): Promise<WorksheetResult> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/student/worksheet`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify({}),
  });
  if (res.status === 409) {
    const data = await res.json().catch(() => ({})) as { message?: string };
    return { status: 'no_sessions', message: data.message ?? 'Complete a few sessions first!' };
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({})) as { message?: string };
    return { status: 'error', message: data.message ?? 'Something went wrong. Please try again.' };
  }
  const data = await res.json() as { questions: WorksheetQuestion[]; subjects: string[] };
  return { status: 'ok', questions: data.questions, subjects: data.subjects };
}

// ─── Parent: edit child learning profile ──────────────────────────────────────

export async function updateChildProfile(
  childId: string,
  fields: Partial<Pick<StudentProfile, 'firstName' | 'grade' | 'subjects' | 'shortTermGoal' | 'longTermGoal' | 'weeklyHours' | 'interests' | 'careerGoal' | 'parentFocus' | 'parentEncouragement'>> & { remindersOptOut?: boolean }
): Promise<void> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/parent/children/${childId}/profile`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify(fields),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? 'Failed to update profile');
  }
}

// "Ask about your child" — parent Q&A grounded in the child's session history.
export async function askAboutChild(childId: string, question: string): Promise<{ answer: string; basedOnSessions: number }> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/parent/children/${childId}/sessions?ask=${encodeURIComponent(question)}`, {
    headers: authHeader ? { Authorization: authHeader } : {},
  });
  if (!res.ok) throw new Error('Failed to get an answer');
  return res.json() as Promise<{ answer: string; basedOnSessions: number }>;
}

// ─── Quiz Scores ──────────────────────────────────────────────────────────────

export async function saveQuizScore(sessionId: string, correct: number, total: number): Promise<void> {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_BASE}/sessions/${sessionId}/score`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(authHeader ? { Authorization: authHeader } : {}) },
    body: JSON.stringify({ correct, total }),
  });
  if (!res.ok) throw new Error('Failed to save quiz score');
}
